import React, {useState} from 'react';

import {fetch} from 'molbio/dao/FeatureLibraryDAO';
import {FeatureLibrary} from 'molbio/feature-libraries/FeatureLibraryConstants';
import notifications from 'notifications';
import {AsyncStatus, useAsyncStateOnMount} from 'ui-library/hooks/useAsyncState';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';

export interface FeatureLibraryModalProps {
  onRequestSubmit: (featureLibraryIds: Array<number>) => void;
  onRequestClose: () => void;
}

export function FeatureLibraryModal({
  onRequestSubmit,
  onRequestClose,
}: FeatureLibraryModalProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [selectedFeatureLibraries, setSelectedFeatureLibraries] = useState<Array<FeatureLibrary>>([]);
  const selectedIds = new Set(selectedFeatureLibraries.map((featureLibrary) => featureLibrary.id));
  const featureLibrariesState = useAsyncStateOnMount(fetch);
  const modalProps = {
    buttons: [{text: 'Auto-annotate', submit: true, disabled: selectedFeatureLibraries.length === 0}],
    onRequestClose,
    onRequestSubmit: () => {
      onRequestSubmit(selectedFeatureLibraries.map((fl: FeatureLibrary) => fl.id));
    },
    title: 'Select Feature Libraries to use in auto-annotation',
  };

  if (featureLibrariesState.status === AsyncStatus.ERROR) {
    notifications.error('Encountered error loading feature libraries.');
    onRequestClose();
    return <></>;
  } else if (featureLibrariesState.status === AsyncStatus.SUCCESS) {
    return (
      <Modal {...modalProps}>
        <div>
          <a
            onClick={() => {
              setSelectedFeatureLibraries(featureLibrariesState.data);
            }}
          >
            Select all
          </a>
          {' / '}
          <a
            onClick={() => {
              setSelectedFeatureLibraries([]);
            }}
          >
            Clear selection
          </a>
          <ChipSelectInput
            query={query}
            onQueryChange={(e) => {
              setQuery(e.target.value);
            }}
            scrollable={true}
            autoFocus={false}
            isMulti={true}
            optionDatas={featureLibrariesState.data
              .filter(
                (featureLibrary) =>
                  !selectedIds.has(featureLibrary.id) &&
                  featureLibrary.name.toLowerCase().includes(query.toLowerCase())
              )
              .map((featureLibrary: FeatureLibrary) => ({
                name: featureLibrary.name,
                value: featureLibrary,
                metadata: featureLibrary.description,
              }))}
            getChipProps={(featureLibrary) => ({text: featureLibrary.name})}
            value={selectedFeatureLibraries}
            onSelect={(featureLibrary: FeatureLibrary) => {
              setSelectedFeatureLibraries([...selectedFeatureLibraries, featureLibrary]);
            }}
            onRemove={(featureLibrary: FeatureLibrary) => {
              setSelectedFeatureLibraries(
                selectedFeatureLibraries.filter((fl) => fl.id !== featureLibrary.id)
              );
            }}
            placeholder="Select feature libraries"
          />
        </div>
      </Modal>
    );
  } else {
    return (
      <Modal {...modalProps}>
        <Spinner />
      </Modal>
    );
  }
}

export default FeatureLibraryModal;
